import { gql } from '@apollo/client/core';
import { getSharedApolloClient } from '@/lib/apollo_setup';

/** Resolves a U.S. postal code to `GeocodeResult` (Empire `geocoding.geocode`). */
const GEOCODE_QUERY = gql`
  query GeocodeQuery($input: GeocodeInput!) {
    geocoding {
      geocode(input: $input) {
        location {
          x
          y
        }
        zipCode
        city
        state
        address
      }
    }
  }
`;

/** Longitude (x) and latitude (y), same convention as `GeoPointInput`. */
export interface Coordinates {
  x: number;
  y: number;
}

/** Matches `GeocodeResult` GraphQL: nested `location` plus mailing fields. */
export interface GeocodeResult {
  location: Coordinates;
  zipCode: string;
  city: string;
  state: string;
  address: string;
}

export interface GeocodeQueryResponse {
  geocoding?: {
    geocode?: {
      location: {
        x: string;
        y: string;
      };
      zipCode: string;
      city: string;
      state: string;
      address: string;
    } | null;
  } | null;
}

/** Client-side U.S. ZIP check (format + obvious placeholders). Does not call the server. */
export function isValidZipcode(zipcode: string | null | undefined): boolean {
  if (!zipcode) return false;

  // Must be exactly 5 digits
  if (!/^\d{5}$/.test(zipcode)) return false;

  // Reject obvious fake/test ZIPs like 00000, 11111, etc.
  if (/^(\d)\1{4}$/.test(zipcode)) return false;

  return true;
}

/** Forward ZIP → point + mailing fields via Empire GraphQL. */
export async function geocode(zipCode: string | null | undefined): Promise<GeocodeResult | null> {
  if (!zipCode || !isValidZipcode(zipCode)) {
    return null;
  }

  const { data, errors } = await getSharedApolloClient().query<GeocodeQueryResponse>({
    query: GEOCODE_QUERY,
    variables: { input: { zipCode } },
    errorPolicy: 'all',
  });

  if (errors?.length) {
    return null;
  }

  const geocodeData = data?.geocoding?.geocode;
  if (!geocodeData) return null;

  const loc = geocodeData?.location;
  if (loc == null || loc.x == null || loc.y == null) {
    return null;
  }

  const x = Number(loc.x);
  const y = Number(loc.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }

  return {
    location: { x, y },
    zipCode: geocodeData.zipCode,
    city: geocodeData.city,
    state: geocodeData.state,
    address: geocodeData.address,
  };
}
